/**
 * Mapa unificado de status — apresentação visual (label, categoria, variante, ícone).
 * Lógica de negócio (claims, ações) permanece nos constants de cada módulo.
 *
 * Variantes semânticas:
 * - info: em andamento / informativo
 * - warning: aguardando ação do usuário ou de terceiros
 * - returned: devolvido / necessita ajuste
 * - success: aprovado / concluído
 * - danger: cancelado / recusado / erro
 * - neutral: rascunho / sem status
 */

import { STATUS_ICONS, STATUS_VARIANTS, stylesForVariant } from '@packages/design-system';

export { STATUS_ICONS, STATUS_VARIANTS, stylesForVariant };

/**
 * @param {string} label
 * @param {string} variant
 * @param {{ icon?: string, priority?: number }} [opts]
 */
function entry(label, variant, opts = {}) {
    return {
        label,
        variant,
        icon: opts.icon || STATUS_ICONS[variant] || '',
        priority: opts.priority ?? 2,
    };
}

/** @type {Record<string, { label: string, variant: string, icon?: string, priority?: number }>} */
const MOT_STATUS_MAP = Object.freeze({
    EM_ANALISE_GERENTE_UNIDADE: entry('Em análise Gerente Unidade', STATUS_VARIANTS.INFO),
    EM_ANALISE_UGP: entry('Em análise Apoio UGP', STATUS_VARIANTS.INFO),
    EM_ANALISE_GERENCIA_UGP: entry('Em análise Gerência UGP', STATUS_VARIANTS.INFO),
    AGUARDANDO_APROVACAO_DIRETORIA: entry('Aguardando aprovação Diretoria', STATUS_VARIANTS.WARNING, { priority: 3 }),
    DEVOLVIDO_SOLICITANTE: entry('Devolvido ao solicitante', STATUS_VARIANTS.RETURNED, { priority: 4 }),
    DEVOLVIDO_SOLICITANTE_APOIO_UGP: entry('Devolvido ao solicitante pelo Apoio UGP', STATUS_VARIANTS.RETURNED, { priority: 4 }),
    DEVOLVIDO_APOIO_UGP: entry('Devolvido ao Apoio UGP', STATUS_VARIANTS.RETURNED, { priority: 4 }),
    APROVADO: entry('Aprovado', STATUS_VARIANTS.SUCCESS, { priority: 1 }),
    CANCELADO: entry('Cancelado', STATUS_VARIANTS.DANGER, { priority: 5 }),
});

/** Hospedagem — chaves alinhadas a STATUS_SOLICITACAO (status.js) */
const HOSPEDAGEM_STATUS_MAP = Object.freeze({
    PENDENTE: entry('Pendente', STATUS_VARIANTS.NEUTRAL),
    APROVADO: entry('Aprovado', STATUS_VARIANTS.SUCCESS),
    REPROVADO: entry('Devolvido para ajustes', STATUS_VARIANTS.DANGER),
    CANCELADO: entry('Cancelado', STATUS_VARIANTS.DANGER),
    AGUARDANDO_COTACAO: entry('Aguardando cotação', STATUS_VARIANTS.WARNING),
    ARGUARDANDO_COTACAO: entry('Aguardando cotação', STATUS_VARIANTS.WARNING),
    COTACAO_ENVIADA: entry('Cotação enviada', STATUS_VARIANTS.INFO),
    DEVOLVIDA_PELA_GERENCIA: entry('Devolvida pela gerência', STATUS_VARIANTS.RETURNED),
    AJUSTAR_SOLICITACAO: entry('Ajustar solicitação', STATUS_VARIANTS.RETURNED),
    AJUSTAR_LISTA_HOSPEDES: entry('Ajustar lista de participantes', STATUS_VARIANTS.RETURNED),
    AJUSTAR_COTACAO: entry('Ajustar cotação', STATUS_VARIANTS.RETURNED),
    SOLICITACAO_EM_ANALISE: entry('Solicitação em análise', STATUS_VARIANTS.INFO),
    AGUARDANDO_APROVACAO_GERENCIA: entry('Aguardando aprovação', STATUS_VARIANTS.WARNING),
    AGUARDANDO_LISTA_HOSPEDES: entry('Aguardando lista de participantes', STATUS_VARIANTS.WARNING),
    AGUARDANDO_ANALISE_LISTA: entry('Aguardando análise da lista', STATUS_VARIANTS.WARNING),
    AGUARDANDO_VOUCHER: entry('Aguardando voucher', STATUS_VARIANTS.WARNING),
    VOUCHER_ENVIADO: entry('Voucher enviado', STATUS_VARIANTS.SUCCESS),
    SOLICITACAO_FINALIZADA: entry('Solicitação finalizada', STATUS_VARIANTS.SUCCESS),
    LISTA_HOSPEDES_ENVIADA: entry('Lista de participantes enviada', STATUS_VARIANTS.INFO),
    // RN38: tag da UABS para solicitação aberta fora do prazo mínimo.
    FORA_DO_PRAZO_MARCA: entry('Fora do prazo', STATUS_VARIANTS.DANGER),
    // RF30/RN53: pill do prazo sem multa na listagem — o rótulo (a data) vem da linha; só o vencido é vermelho.
    PRAZO_SEM_MULTA: entry('Prazo sem multa', STATUS_VARIANTS.NEUTRAL, { icon: 'pi pi-calendar' }),
    PRAZO_SEM_MULTA_VENCIDO: entry('Prazo sem multa vencido', STATUS_VARIANTS.DANGER, { icon: 'pi pi-exclamation-triangle' }),
    PRAZO_SEM_MULTA_A_VENCER: entry('Prazo sem multa a vencer', STATUS_VARIANTS.WARNING, { icon: 'pi pi-clock' }),
    PRAZO_SEM_MULTA_NADA_RESERVADO: entry('Nada reservado', STATUS_VARIANTS.NEUTRAL, { icon: 'pi pi-minus-circle' }),
    // RF22/RF30: sem prazo e sem a marca "nada reservado" — mesmo pill neutro do vazio.
    PRAZO_SEM_MULTA_NAO_INFORMADO: entry('Não informado', STATUS_VARIANTS.NEUTRAL, { icon: 'pi pi-minus-circle' }),
    // Status novos da V3 (fluxos de alteração e cancelamento)
    ALTERACAO_SOLICITADA: entry('Alteração solicitada', STATUS_VARIANTS.WARNING),
    CANCELAMENTO_SOLICITADO: entry('Cancelamento solicitado', STATUS_VARIANTS.WARNING),
    AGUARDANDO_CANCELAMENTO: entry('Aguardando cancelamento', STATUS_VARIANTS.RETURNED),
    // RF06: a agência iniciou a cotação — em andamento.
    EM_COTACAO: entry('Em cotação', STATUS_VARIANTS.INFO),
    // Chaves numéricas (API)
    0: entry('Pendente', STATUS_VARIANTS.NEUTRAL),
    1: entry('Aprovado', STATUS_VARIANTS.SUCCESS),
    2: entry('Devolvido para ajustes', STATUS_VARIANTS.DANGER),
    3: entry('Cancelado', STATUS_VARIANTS.DANGER),
    4: entry('Aguardando cotação', STATUS_VARIANTS.WARNING),
    5: entry('Cotação enviada', STATUS_VARIANTS.INFO),
    6: entry('Devolvida pela gerência', STATUS_VARIANTS.RETURNED),
    7: entry('Ajustar solicitação', STATUS_VARIANTS.RETURNED),
    8: entry('Ajustar lista de participantes', STATUS_VARIANTS.RETURNED),
    9: entry('Ajustar cotação', STATUS_VARIANTS.RETURNED),
    10: entry('Solicitação em análise', STATUS_VARIANTS.INFO),
    11: entry('Aguardando aprovação', STATUS_VARIANTS.WARNING),
    12: entry('Aguardando lista de participantes', STATUS_VARIANTS.WARNING),
    13: entry('Aguardando análise da lista', STATUS_VARIANTS.WARNING),
    14: entry('Aguardando voucher', STATUS_VARIANTS.WARNING),
    15: entry('Voucher enviado', STATUS_VARIANTS.SUCCESS),
    16: entry('Solicitação finalizada', STATUS_VARIANTS.SUCCESS),
    17: entry('Lista de participantes enviada', STATUS_VARIANTS.INFO),
    18: entry('Alteração solicitada', STATUS_VARIANTS.WARNING),
    19: entry('Cancelamento solicitado', STATUS_VARIANTS.WARNING),
    20: entry('Aguardando cancelamento', STATUS_VARIANTS.RETURNED),
    21: entry('Em cotação', STATUS_VARIANTS.INFO),
});

/** Tramitações jurídicas — chaves do status da API */
const TRAMITACAO_STATUS_MAP = Object.freeze({
    CADASTRADO: entry('Cadastrado', STATUS_VARIANTS.INFO),
    DEVOLVIDO: entry('Devolvido', STATUS_VARIANTS.RETURNED),
    ENVIADO: entry('Enviado', STATUS_VARIANTS.WARNING),
    FINALIZADO: entry('Finalizado', STATUS_VARIANTS.SUCCESS),
    CANCELADO: entry('Cancelado', STATUS_VARIANTS.DANGER),
});

/** Translado/Veículo — chaves de STATUS_SOLICITACAO (status.js) */
const TRANSLADO_STATUS_MAP = Object.freeze({
    ANALISE_UADM: entry('Em análise UADM', STATUS_VARIANTS.INFO),
    DEVOLVIDA: entry('Devolvida', STATUS_VARIANTS.RETURNED),
    EM_COTACAO: entry('Em cotação', STATUS_VARIANTS.WARNING),
    FINALIZADA: entry('Finalizada', STATUS_VARIANTS.SUCCESS),
});

/** Benefícios — StatusSolicitacao (enums.js) — chaves numéricas e texto */
const BENEFICIOS_STATUS_MAP = Object.freeze({
    PENDENTE: entry('Pendente', STATUS_VARIANTS.WARNING),
    APROVADO: entry('Aprovado', STATUS_VARIANTS.SUCCESS),
    DEVOLVIDO: entry('Devolvido', STATUS_VARIANTS.RETURNED),
    REPROVADO: entry('Reprovado', STATUS_VARIANTS.DANGER),
    CANCELADO: entry('Cancelado', STATUS_VARIANTS.DANGER),
    1: entry('Pendente', STATUS_VARIANTS.WARNING),
    2: entry('Aprovado', STATUS_VARIANTS.SUCCESS),
    3: entry('Devolvido', STATUS_VARIANTS.RETURNED),
    4: entry('Reprovado', STATUS_VARIANTS.DANGER),
    5: entry('Cancelado', STATUS_VARIANTS.DANGER),
    ATIVO: entry('Ativo', STATUS_VARIANTS.SUCCESS),
    INATIVO: entry('Inativo', STATUS_VARIANTS.NEUTRAL),
});

/** Controle de acesso — usuários, aplicações */
const CONTROLE_ACESSO_STATUS_MAP = Object.freeze({
    ATIVO: entry('Ativo', STATUS_VARIANTS.SUCCESS),
    INATIVO: entry('Inativo', STATUS_VARIANTS.NEUTRAL),
    0: entry('Inativo', STATUS_VARIANTS.NEUTRAL),
    1: entry('Ativo', STATUS_VARIANTS.SUCCESS),
});

/** Webster / Check-ins — sincronismo de eventos e booleanos SIM/NÃO */
const CHECKINS_EVENTOS_STATUS_MAP = Object.freeze({
    ATIVO: entry('Ativo', STATUS_VARIANTS.SUCCESS),
    INATIVO: entry('Inativo', STATUS_VARIANTS.DANGER),
    SIM: entry('Sim', STATUS_VARIANTS.SUCCESS),
    NAO: entry('Não', STATUS_VARIANTS.DANGER),
    NÃO: entry('Não', STATUS_VARIANTS.DANGER),
    'ERRO NA INTEGRAÇÃO': entry('Erro na integração', STATUS_VARIANTS.DANGER),
    'EM PROCESSAMENTO': entry('Em processamento', STATUS_VARIANTS.WARNING),
    'AGUARDANDO PROCESSAMENTO': entry('Aguardando processamento', STATUS_VARIANTS.WARNING),
    'NÃO APLICÁVEL': entry('Não aplicável', STATUS_VARIANTS.NEUTRAL),
    'ENCERRADO NO FOCO': entry('Encerrado no Foco', STATUS_VARIANTS.NEUTRAL),
    true: entry('Sim', STATUS_VARIANTS.SUCCESS),
    false: entry('Não', STATUS_VARIANTS.DANGER),
});

/** Webster / Check-ins — contas de eventos */
const CHECKINS_CONTAS_STATUS_MAP = Object.freeze({
    ATIVO: entry('Ativo', STATUS_VARIANTS.SUCCESS),
    INATIVO: entry('Inativo', STATUS_VARIANTS.NEUTRAL),
    0: entry('Inativo', STATUS_VARIANTS.NEUTRAL),
    1: entry('Ativo', STATUS_VARIANTS.SUCCESS),
});

/** Webster / Check-ins — integração de participantes, vínculos e check-ins */
const CHECKINS_INTEGRACAO_STATUS_MAP = Object.freeze({
    INTEGRADO: entry('Integrado', STATUS_VARIANTS.SUCCESS),
    'INTEGRADO PF': entry('Integrado PF', STATUS_VARIANTS.SUCCESS),
    'INTEGRADO PF/PJ': entry('Integrado PF/PJ', STATUS_VARIANTS.SUCCESS),
    VINCULADO: entry('Vinculado', STATUS_VARIANTS.SUCCESS),
    VÁLIDO: entry('Válido', STATUS_VARIANTS.SUCCESS),
    'FALHA INTEGRACAO': entry('Falha na integração', STATUS_VARIANTS.WARNING),
    'FALHA NA VINCULAÇÃO': entry('Falha na vinculação', STATUS_VARIANTS.WARNING),
    'FALHA NA INTEGRAÇÃO': entry('Falha na integração', STATUS_VARIANTS.WARNING),
    'ERRO NA INTEGRAÇÃO': entry('Erro na integração', STATUS_VARIANTS.DANGER),
    INVÁLIDO: entry('Inválido', STATUS_VARIANTS.DANGER),
    'INVÁLIDO EM PROCESSAMENTO': entry('Inválido em processamento', STATUS_VARIANTS.DANGER),
    'EM PROCESSAMENTO': entry('Em processamento', STATUS_VARIANTS.WARNING),
    'AGUARDANDO INTEGRAÇÃO': entry('Aguardando integração', STATUS_VARIANTS.WARNING),
    'AGUARDANDO SOLICITAÇÃO DE INTEGRAÇÃO': entry('Aguardando solicitação', STATUS_VARIANTS.WARNING),
    'NÃO APLICÁVEL': entry('Não aplicável', STATUS_VARIANTS.NEUTRAL),
    CONCLUIDO: entry('Concluído', STATUS_VARIANTS.SUCCESS),
    'NÃO COMPARECIDO': entry('Não comparecido', STATUS_VARIANTS.DANGER),
    'NAO CONSTA': entry('Não consta', STATUS_VARIANTS.NEUTRAL),
    CADASTRADO: entry('Cadastrado', STATUS_VARIANTS.SUCCESS),
    'NÃO CADASTRADO': entry('Não cadastrado', STATUS_VARIANTS.DANGER),
    ÓRFÃO: entry('Órfão', STATUS_VARIANTS.DANGER),
    0: entry('Não consta', STATUS_VARIANTS.NEUTRAL),
    1: entry('Concluído', STATUS_VARIANTS.SUCCESS),
    2: entry('Não comparecido', STATUS_VARIANTS.DANGER),
});

/** Webster / Check-ins — expositores */
const CHECKINS_EXPOSITORES_STATUS_MAP = Object.freeze({
    0: entry('Inscrito', STATUS_VARIANTS.INFO),
    1: entry('Habilitado', STATUS_VARIANTS.SUCCESS),
    INSCRITO: entry('Inscrito', STATUS_VARIANTS.INFO),
    HABILITADO: entry('Habilitado', STATUS_VARIANTS.SUCCESS),
});

/** Webster / Check-ins — impressoras */
const CHECKINS_IMPRESSORAS_STATUS_MAP = Object.freeze({
    0: entry('Indisponível', STATUS_VARIANTS.DANGER),
    1: entry('Disponível', STATUS_VARIANTS.SUCCESS),
});

/** Mapa Parceiros — parcerias (Parça) */
const PARCERIA_STATUS_MAP = Object.freeze({
    0: entry('Em elaboração', STATUS_VARIANTS.WARNING),
    1: entry('Em execução', STATUS_VARIANTS.INFO),
    2: entry('Cancelado', STATUS_VARIANTS.DANGER),
    3: entry('Concluído', STATUS_VARIANTS.SUCCESS),
    4: entry('Suspenso', STATUS_VARIANTS.WARNING),
    5: entry('Excluído', STATUS_VARIANTS.DANGER),
    6: entry('Assinatura pendente', STATUS_VARIANTS.WARNING),
    7: entry('Assinado parcial', STATUS_VARIANTS.WARNING),
    8: entry('Acordo assinado', STATUS_VARIANTS.SUCCESS),
    9: entry('Assinatura rejeitada', STATUS_VARIANTS.DANGER),
    10: entry('Expirado', STATUS_VARIANTS.DANGER),
    11: entry('Aditivo pendente', STATUS_VARIANTS.WARNING),
    12: entry('Acordo rejeitado', STATUS_VARIANTS.DANGER),
    EM_ELABORACAO: entry('Em elaboração', STATUS_VARIANTS.WARNING),
    EM_EXECUCAO: entry('Em execução', STATUS_VARIANTS.INFO),
    CANCELADO: entry('Cancelado', STATUS_VARIANTS.DANGER),
    CONCLUIDO: entry('Concluído', STATUS_VARIANTS.SUCCESS),
    SUSPENSO: entry('Suspenso', STATUS_VARIANTS.WARNING),
    EXCLUIDO: entry('Excluído', STATUS_VARIANTS.DANGER),
    ASSINATURA_PENDENTE: entry('Assinatura pendente', STATUS_VARIANTS.WARNING),
    ASSINADO_PARCIAL: entry('Assinado parcial', STATUS_VARIANTS.WARNING),
    ACORDO_ASSINADO: entry('Acordo assinado', STATUS_VARIANTS.SUCCESS),
    ACORDO_TOTALMENTE_ASSINADO: entry('Acordo totalmente assinado', STATUS_VARIANTS.SUCCESS),
    ASSINATURA_REJEITADA: entry('Assinatura rejeitada', STATUS_VARIANTS.DANGER),
    ADITIVO_PENDENTE: entry('Aditivo pendente', STATUS_VARIANTS.WARNING),
    EXPIRADO: entry('Expirado', STATUS_VARIANTS.DANGER),
    ACORDO_REJEITADO: entry('Acordo rejeitado', STATUS_VARIANTS.DANGER),
});

const PARCERIA_STATUS_ALIASES = Object.freeze({
    ASSINADO_PARCIALMENTE: 'ASSINADO_PARCIAL',
});

/** Licitações — chaves numéricas alinhadas a `etapasSolicitacaoEnum` (proximaEtapa) */
function licitacaoVariantPorEtapa(etapa) {
    const n = Number(etapa);
    if (Number.isNaN(n) || n === 0) return STATUS_VARIANTS.NEUTRAL;
    if (n === 31) return STATUS_VARIANTS.DANGER;
    if (n === 33 || n === 35) return STATUS_VARIANTS.SUCCESS;
    if (n === 34) return STATUS_VARIANTS.WARNING;
    if (n >= 4 && n <= 9) return STATUS_VARIANTS.RETURNED;
    if (n === 12) return STATUS_VARIANTS.RETURNED;
    if (n >= 1 && n <= 3) return STATUS_VARIANTS.WARNING;
    return STATUS_VARIANTS.INFO;
}

const LICITACAO_ETAPAS = Object.freeze({
    1: 'Aguardando classificação',
    2: 'Classificação',
    3: 'Reclassificar',
    4: 'Solicitar ajustes da solicitação',
    5: 'Documentação do processo',
    6: 'Conformidade legal',
    7: 'Complementar documentação',
    8: 'Ajustar edital',
    9: 'Ajustar solicitação',
    10: 'Analisar TR',
    11: 'Analisar documentação do processo',
    12: 'Documentação do processo precisa de ajustes',
    13: 'Instrução cotação',
    14: 'Documentação da empresa',
    15: 'Documentação recebida',
    16: 'Elaborar edital',
    17: 'Elaborar justificativa',
    18: 'Validar edital',
    19: 'TR enviado para assinatura',
    20: 'Receber assinatura',
    21: 'Finalizar edital',
    22: 'Validar justificativa',
    23: 'Justificativa aprovada',
    24: 'Publicar edital',
    25: 'Sessão pública',
    26: 'Recurso',
    27: 'Solicitar parecer',
    28: 'Elaborar parecer jurídico',
    29: 'Encaminhar para análise jurídica',
    30: 'Processo em homologação',
    31: 'Solicitação cancelada',
    32: 'Comprometer contratação',
    33: 'Solicitação finalizada',
    34: 'Contrato em assinatura',
    35: 'Contrato assinado',
});

/** @type {Record<string|number, { label: string, variant: string, icon?: string, priority?: number }>} */
const LICITACAO_STATUS_MAP = Object.freeze(
    Object.fromEntries(Object.entries(LICITACAO_ETAPAS).map(([etapa, label]) => [Number(etapa), entry(label, licitacaoVariantPorEtapa(etapa))]))
);

/** Expressos — StatusExibicaoEnum da API (numérico); labels espelham as Description do back */
const EXPRESSOS_STATUS_MAP = Object.freeze({
    1: entry('Pendente', STATUS_VARIANTS.WARNING),
    2: entry('Integrado', STATUS_VARIANTS.SUCCESS),
    3: entry('Erro', STATUS_VARIANTS.DANGER),
    4: entry('Não integrável', STATUS_VARIANTS.NEUTRAL),
    // SituacaoSalaEnum — a API serializa como string ('Apta'/'Incompleta'); calculada pelo back, nunca derivar no front
    APTA: entry('Apta', STATUS_VARIANTS.SUCCESS),
    INCOMPLETA: entry('Incompleta', STATUS_VARIANTS.WARNING),
});

/** Mapas por domínio — injetados em `app.use(SebraeDesignSystem, { statusMaps })`. */
export const DOMAIN_STATUS_MAPS = Object.freeze({
    mot: MOT_STATUS_MAP,
    hospedagem: HOSPEDAGEM_STATUS_MAP,
    tramitacao: TRAMITACAO_STATUS_MAP,
    translado: TRANSLADO_STATUS_MAP,
    beneficios: BENEFICIOS_STATUS_MAP,
    controleacesso: CONTROLE_ACESSO_STATUS_MAP,
    parceria: PARCERIA_STATUS_MAP,
    licitacao: LICITACAO_STATUS_MAP,
    checkinsEventos: CHECKINS_EVENTOS_STATUS_MAP,
    checkinsContas: CHECKINS_CONTAS_STATUS_MAP,
    checkinsIntegracao: CHECKINS_INTEGRACAO_STATUS_MAP,
    checkinsExpositores: CHECKINS_EXPOSITORES_STATUS_MAP,
    checkinsImpressoras: CHECKINS_IMPRESSORAS_STATUS_MAP,
    expressos: EXPRESSOS_STATUS_MAP,
});

/** @deprecated use DOMAIN_STATUS_MAPS */
const DOMAIN_MAPS = DOMAIN_STATUS_MAPS;

/** Alias de chaves vindas da API (ex.: AGUARDANDO-COTACAO → ARGUARDANDO_COTACAO) */
const HOSPEDAGEM_STATUS_ALIASES = Object.freeze({
    AGUARDANDO_COTACAO: 'ARGUARDANDO_COTACAO',
    // Descrições V3 da API (rótulo "participantes"); as chaves internas não mudam.
    AGUARDANDO_LISTA_DE_PARTICIPANTES: 'AGUARDANDO_LISTA_HOSPEDES',
    AJUSTAR_LISTA_DE_PARTICIPANTES: 'AJUSTAR_LISTA_HOSPEDES',
    AGUARDANDO_LISTA_DE_HOSPEDES: 'AGUARDANDO_LISTA_HOSPEDES',
    AJUSTAR_LISTA_DE_HOSPEDES: 'AJUSTAR_LISTA_HOSPEDES',
});

/** Aliases por domínio — injetados no plugin do DS. */
export const DOMAIN_STATUS_ALIASES = Object.freeze({
    hospedagem: HOSPEDAGEM_STATUS_ALIASES,
    parceria: PARCERIA_STATUS_ALIASES,
});

/**
 * Normaliza texto/chave de status para lookup no mapa.
 * @param {string|number} status
 * @returns {string}
 */
export function normalizeStatusKey(status) {
    return String(status ?? '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[_\s-]+/g, '_');
}

/**
 * @param {Record<string|number, object>} map
 * @param {string|number} statusKey
 */
function lookupStatus(map, statusKey) {
    if (statusKey === 0 || statusKey === '0') {
        return map[0] ?? null;
    }

    if (map[statusKey]) {
        return map[statusKey];
    }

    const str = String(statusKey ?? '').trim();
    if (!str) {
        return null;
    }

    if (map[str]) {
        return map[str];
    }

    const normalized = normalizeStatusKey(str);
    if (map[normalized]) {
        return map[normalized];
    }

    const alias = HOSPEDAGEM_STATUS_ALIASES[normalized];
    if (alias && map[alias]) {
        return map[alias];
    }

    return null;
}

/**
 * Resolve a chave de status de hospedagem a partir do valor numérico ou descrição da API.
 * @param {string} [statusDescricao]
 * @param {string|number|null} [statusNumerico]
 * @returns {string|number}
 */
export function hospedagemStatusKey(statusDescricao, statusNumerico = null) {
    if (statusNumerico != null && statusNumerico !== '' && resolveStatus('hospedagem', statusNumerico)) {
        return statusNumerico;
    }

    const fromDescricao = normalizeStatusKey(statusDescricao);
    if (fromDescricao && resolveStatus('hospedagem', fromDescricao)) {
        return fromDescricao;
    }

    const alias = HOSPEDAGEM_STATUS_ALIASES[fromDescricao];
    if (alias) {
        return alias;
    }

    if (statusNumerico != null && statusNumerico !== '') {
        return statusNumerico;
    }

    return fromDescricao || '';
}

/**
 * Resolve a chave de status de licitação a partir da descrição ou etapa numérica da API.
 * @param {string} [descricaoProximaEtapa]
 * @param {string|number|null} [proximaEtapa]
 * @returns {string|number}
 */
export function licitacaoStatusKey(descricaoProximaEtapa, proximaEtapa = null) {
    if (proximaEtapa != null && proximaEtapa !== '' && resolveStatus('licitacao', proximaEtapa)) {
        return proximaEtapa;
    }

    const fromDescricao = normalizeStatusKey(descricaoProximaEtapa);
    if (fromDescricao && resolveStatus('licitacao', fromDescricao)) {
        return fromDescricao;
    }

    if (proximaEtapa != null && proximaEtapa !== '') {
        return proximaEtapa;
    }

    return fromDescricao || '';
}

/**
 * @param {string} domain
 * @param {string|number} statusKey
 * @returns {{ label: string, variant: string, icon?: string, priority?: number } | null}
 */
export function resolveStatus(domain, statusKey) {
    const map = DOMAIN_MAPS[domain];
    if (!map) return null;
    return lookupStatus(map, statusKey);
}

/**
 * @param {string} domain
 * @param {string|number} statusKey
 * @returns {string}
 */
export function labelForStatus(domain, statusKey) {
    const resolved = resolveStatus(domain, statusKey);
    return resolved?.label ?? String(statusKey ?? '-');
}

/**
 * @param {string} domain
 * @param {string|number} statusKey
 * @returns {string}
 */
export function iconForStatus(domain, statusKey) {
    const resolved = resolveStatus(domain, statusKey);
    if (resolved?.icon) return resolved.icon;
    if (resolved?.variant) return STATUS_ICONS[resolved.variant] || '';
    return STATUS_ICONS[STATUS_VARIANTS.NEUTRAL];
}

/**
 * Normaliza filtro de status (valor único ou array) para lista de valores da API.
 * @param {string|number|Array<string|number>|null|undefined} status
 * @returns {Array<string|number>}
 */
export function statusFilterValues(status) {
    if (status == null || status === '') return [];
    if (Array.isArray(status)) return status.filter((s) => s != null && s !== '');
    return [status];
}

export {
    MOT_STATUS_MAP,
    HOSPEDAGEM_STATUS_MAP,
    TRAMITACAO_STATUS_MAP,
    TRANSLADO_STATUS_MAP,
    BENEFICIOS_STATUS_MAP,
    PARCERIA_STATUS_MAP,
    LICITACAO_STATUS_MAP,
    CHECKINS_EVENTOS_STATUS_MAP,
    CHECKINS_CONTAS_STATUS_MAP,
    CHECKINS_INTEGRACAO_STATUS_MAP,
    CHECKINS_EXPOSITORES_STATUS_MAP,
    CHECKINS_IMPRESSORAS_STATUS_MAP,
};
